/**
 * `/admin` - Administration: everything that belongs to whoever runs the app,
 * in one place, behind a rail rather than a tab row.
 *
 * It is a rail because this list grows. It began as one toggle screen, has four
 * sections today and gains people, groups, export jobs and assistant usage as
 * `docs/plans/postgresql.md` proceeds; a tab row stops working somewhere around six. The
 * rail also groups them, and the grouping is the point: **Access** decides what
 * people may do, **Application** decides what the app offers.
 *
 * Each section is present only for an account holding its permission, and the
 * API checks the same permission again. There is no longer a code in front of
 * the page - see `state/admin-access.ts` for why that went.
 *
 * * **Users**, **Groups** and **Roles** - who exists, their roles, who is in
 *   which group and what each predefined role allows. Granting and revoking
 *   Administrator is root's alone, enforced by the `admin_grant_guard` trigger
 *   rather than by this screen.
 * * **Features** - the feature flags in `cfg.feature_flag`, which decide what
 *   *everyone* gets, per person, per group or not at all.
 * * **Pages** - a `localStorage` display preference for this browser's
 *   navigation. The one section that is not org-wide, and it says so, because
 *   sitting next to Features invites the opposite assumption. It needs no
 *   permission and no database, so the page still does something useful on a
 *   run with `DATABASE_URL` unset.
 * * **Activity** - the audit log: who did what, when, including refusals.
 *
 * Its own route rather than a section of the app, because it configures the
 * navigation and should not sit inside the thing it configures.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { hasFlag } from '../../state/feature-flags.ts';
import { adminRole } from '../../state/admin-access.ts';
import { canDo, isRootUser, myAccess, myPermissions } from '../../state/store.ts';
import './activity-log.ts';
import './feature-flags-panel.ts';
import './people-panel.ts';
import './groups-panel.ts';
import './roles-panel.ts';
import './transcripts-panel.ts';
import './announcements-panel.ts';
import {
  PAGES,
  canDisable,
  isPageEnabled,
  isPageVisible,
  pageVisibility,
  resetPageVisibility,
  setPageEnabled,
  visibleTabs,
  type PageInfo,
} from '../../state/page-visibility.ts';

type SectionId = 'people' | 'groups' | 'roles' | 'features' | 'pages' | 'activity' | 'transcripts' | 'announcements';

interface AdminSection {
  id: SectionId;
  label: string;
  title: string;
  /** The sentence under the title: what this section decides, and for whom. */
  sub: string;
  /** Which half of the rail it sits in. */
  band: 'access' | 'application';
  /** Absent means everyone who reaches the route - Pages only. */
  permission?: string;
  /** Root and nobody else, whatever permissions an account holds. */
  rootOnly?: boolean;
}

const SECTIONS: readonly AdminSection[] = [
  {
    id: 'people',
    label: 'Users',
    title: 'Users',
    sub: 'Everyone this app knows, their roles, and what they can actually do - with the reason.',
    band: 'access',
    permission: 'users:read',
  },
  {
    id: 'groups',
    label: 'Groups',
    title: 'Groups',
    sub: 'Teams, their leaders and the roles every member inherits, so access is granted once instead of per person.',
    band: 'access',
    permission: 'users:read',
  },
  {
    id: 'roles',
    label: 'Roles',
    title: 'Roles',
    sub: 'The seven predefined roles, what each allows, who holds them - and the role new people start with.',
    band: 'access',
    permission: 'users:read',
  },
  {
    id: 'features',
    label: 'Features',
    title: 'Features',
    sub: 'Who gets which part of the app. Comes before roles: Off is off for everyone, Root included.',
    band: 'application',
    permission: 'flags:read',
  },
  {
    id: 'pages',
    label: 'Pages',
    title: 'Pages',
    sub: 'Choose what appears in the navigation of this browser.',
    band: 'application',
  },
  {
    id: 'activity',
    label: 'Activity',
    title: 'Activity',
    sub: 'What has happened in this app, newest first - including what was refused.',
    band: 'application',
    permission: 'audit:read',
  },
  {
    id: 'announcements',
    label: 'Announcements',
    title: 'Announcements',
    sub: 'Banners and pop-ups everyone sees on every page - maintenance, warnings and release notes.',
    band: 'application',
    permission: 'announcements:write',
  },
  {
    id: 'transcripts',
    label: 'Transcripts',
    title: 'Transcripts',
    sub: "Read what people asked SONAR and what it answered. Root only; every chat you open is recorded in Activity.",
    band: 'application',
    rootOnly: true,
  },
];

const BANDS: ReadonlyArray<{ band: AdminSection['band']; label: string }> = [
  { band: 'access', label: 'Access' },
  { band: 'application', label: 'Application' },
];

/** Sections this account can actually open, in rail order. */
function availableSections(): AdminSection[] {
  return SECTIONS.filter((s) =>
    s.rootOnly ? isRootUser.value : !s.permission || canDo(s.permission),
  );
}

/**
 * The section named in the URL fragment, when it is one this account may open.
 *
 * A fragment rather than a query parameter because `url-sync.ts` owns the query
 * string for the chart, and Administration is not a chart. It makes the account
 * menu able to link straight at People, and a reload keep its place.
 */
function sectionFromHash(): SectionId | null {
  if (typeof location === 'undefined') return null;
  const id = location.hash.replace(/^#/, '') as SectionId;
  return availableSections().some((s) => s.id === id) ? id : null;
}

@customElement('admin-page')
export class AdminPage extends LightElement {
  private store = new StoreController(this, [pageVisibility, myPermissions, isRootUser]);

  @state() private section: SectionId = sectionFromHash() ?? 'pages';
  @state() private justReset = false;

  /**
   * Permissions arrive after the first paint, so the section the link asked for
   * is often not yet allowed when the element mounts. Re-read once they land,
   * but only while the person has not chosen something else in the meantime.
   */
  private picked = sectionFromHash() !== null;

  private onHashChange = (): void => {
    const id = sectionFromHash();
    if (id) {
      this.section = id;
      this.picked = true;
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('hashchange', this.onHashChange);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.onHashChange);
  }

  override willUpdate(): void {
    if (this.picked) return;
    // Land on the first section this account has - People for an administrator,
    // Pages for everyone else, including every account on a run with no
    // database at all.
    const asked = sectionFromHash();
    if (asked) {
      this.section = asked;
      this.picked = true;
      return;
    }
    const first = availableSections()[0];
    if (first) this.section = first.id;
  }

  /** A full navigation, so the app rebuilds cleanly under the new settings. */
  private backToApp(): void {
    window.location.href = '/';
  }

  private select(id: SectionId): void {
    this.section = id;
    this.picked = true;
    // `replaceState`, not a new entry: moving between sections of one screen is
    // not a journey, and Back should leave Administration rather than walk the
    // rail backwards.
    history.replaceState(null, '', `${location.pathname}#${id}`);
  }

  private onReset(): void {
    resetPageVisibility();
    this.justReset = true;
    setTimeout(() => {
      this.justReset = false;
    }, 2500);
  }

  /** Why a toggle is locked, or null when it is free to move. */
  private lockedReason(page: PageInfo): string | null {
    if (page.flag && !hasFlag(page.flag)) {
      // Not a local setting to argue with: Features decides per account on the
      // server, so say where the answer comes from - and why, when we know.
      const why = myAccess.value?.features[page.flag]?.reason;
      return why ? `Not available to this account. ${why}` : 'Not available to this account - see Features.';
    }
    if (isPageEnabled(page.id) && !canDisable(page.id)) {
      return 'The last visible Explore tab cannot be hidden.';
    }
    return null;
  }

  private renderRow(page: PageInfo): TemplateResult {
    // Reflects what the navigation actually shows, not just the stored setting:
    // a flag-gated page whose flag is absent is off, however the setting reads.
    const on = isPageVisible(page.id);
    const locked = this.lockedReason(page);
    return html`
      <label class="admin-row ${locked ? 'is-locked' : ''}">
        <span class="admin-row-text">
          <span class="admin-row-title">
            ${page.label}
            <span class="admin-kind">${page.kind === 'section' ? 'section' : 'Explore tab'}</span>
          </span>
          <span class="admin-row-desc">${locked ?? page.description}</span>
        </span>
        <input
          class="admin-switch"
          type="checkbox"
          role="switch"
          .checked=${on}
          ?disabled=${!!locked}
          aria-label=${`Show ${page.label}`}
          @change=${(e: Event) =>
            setPageEnabled(page.id, (e.target as HTMLInputElement).checked)}
        />
      </label>
    `;
  }

  private renderPages(): TemplateResult {
    const hiddenCount = PAGES.filter((p) => !isPageEnabled(p.id)).length;
    return html`
      <div class="admin-list">${PAGES.map((p) => this.renderRow(p))}</div>
      <footer class="admin-foot">
        <p class="admin-note">
          Saved in this browser only — it is not an org-wide setting or a permission.
          A hidden page's link still works if someone types it.
          ${visibleTabs().length === 1
            ? html`<br />Only one Explore tab is left, so it cannot be hidden.`
            : nothing}
          ${canDo('flags:read')
            ? html`<br />To change a feature for <em>everyone</em>, use Features.`
            : nothing}
        </p>
        <button class="admin-reset" ?disabled=${hiddenCount === 0} @click=${this.onReset}>
          ${this.justReset ? 'Everything is on' : 'Show all pages'}
        </button>
      </footer>
    `;
  }

  /** The rail: sections this account may open, grouped by what they decide. */
  private renderRail(): TemplateResult {
    const available = availableSections();
    const role = adminRole();
    return html`
      <nav class="admin-rail" aria-label="Administration sections">
        <div class="admin-rail-head">
          Administration
          ${role
            ? html`<span class="admin-role-chip is-${role}" title=${
                role === 'root'
                  ? 'Root: the only account that can grant or revoke administrator.'
                  : 'Administrator.'
              }>${role}</span>`
            : nothing}
        </div>
        ${BANDS.map((band) => {
          const items = available.filter((s) => s.band === band.band);
          if (!items.length) return nothing;
          return html`
            <div class="admin-rail-band">
              <div class="admin-rail-band-head">${band.label}</div>
              ${items.map(
                (s) => html`
                  <button
                    class="admin-rail-item ${this.section === s.id ? 'active' : ''}"
                    aria-current=${this.section === s.id ? 'page' : 'false'}
                    @click=${() => this.select(s.id)}
                  >
                    ${s.label}
                  </button>
                `,
              )}
            </div>
          `;
        })}
        <button class="admin-rail-back" @click=${this.backToApp}>← Back to app</button>
      </nav>
    `;
  }

  override render(): TemplateResult {
    const section =
      availableSections().find((s) => s.id === this.section) ??
      SECTIONS.find((s) => s.id === 'pages')!;
    return html`
      <div class="admin-shell">
        <div class="admin-card">
          ${this.renderRail()}
          <div class="admin-body">
            <header class="admin-head">
              <div>
                <h1>${section.title}</h1>
                <p class="admin-sub">${section.sub}</p>
              </div>
            </header>
            ${section.id === 'people' ? html`<people-panel></people-panel>` : nothing}
            ${section.id === 'groups' ? html`<groups-panel></groups-panel>` : nothing}
            ${section.id === 'roles' ? html`<roles-panel></roles-panel>` : nothing}
            ${section.id === 'features' ? html`<feature-flags-panel></feature-flags-panel>` : nothing}
            ${section.id === 'activity' ? html`<activity-log></activity-log>` : nothing}
            ${section.id === 'transcripts' ? html`<transcripts-panel></transcripts-panel>` : nothing}
            ${section.id === 'announcements' ? html`<announcements-panel></announcements-panel>` : nothing}
            ${section.id === 'pages' ? this.renderPages() : nothing}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'admin-page': AdminPage;
  }
}
